import React from 'react';
import { InputBase, RadioButton, ButtonGroup } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import * as chrono from 'chrono-node';
import { Formik, Field, FieldProps, FormikProps } from 'formik';
import DateRange from 'Components/DateRange';
import { shouldShowMerchants } from 'Selectors';
import { DATE_RANGES_TYPES, MerchantReportTypeValue } from 'Common/enums';
import GenerateReportButton from '../GenerateReportButton';
import { processFormValues } from './helpers';
import styles from './NewReportForm.css';

type Props = {
    setFormValues: (values: ReportDataCount) => void;
    config: RepositoryConfigData | null;
    reportType: ReportType;
};

type ValidateError = {
    reportType?: string;
    singleMerchantId?: string;
    dateRange?: DateRangeError;
};

const validate = (values: NewMerchantReportFormValues) => {
    const errors: ValidateError = {};
    if (!values.reportType) errors.reportType = 'Required';
    if (values.reportType === MerchantReportTypeValue.singleMerchant && !values.singleMerchantId)
        errors.singleMerchantId = 'Required';
    const dateRangeErrors: DateRangeError = {};
    const { dateRangeType, dateRangeValue, dateRangeMinValue } = values.dateRange;

    if (!dateRangeType) dateRangeErrors.dateRangeType = 'Required';

    values.dateRange.isOutOfMinRange = false;

    switch (dateRangeType) {
        case DATE_RANGES_TYPES.CUSTOM: {
            const customDateRangeError: CustomDateRangeError = {};
            if (!(dateRangeValue as CustomDateRange)?.startDate) {
                customDateRangeError.startDate = 'Required';
            }
            if (!(dateRangeValue as CustomDateRange)?.endDate) {
                customDateRangeError.endDate = 'Required';
            }
            if (!_.isEmpty(customDateRangeError)) {
                (dateRangeErrors.dateRangeValue as CustomDateRangeError) = customDateRangeError;
            }
            values.dateRange.isOutOfMinRange = dateRangeMinValue > (dateRangeValue as CustomDateRange)?.startDate;
            break;
        }
        case DATE_RANGES_TYPES.PAST_DAYS: {
            if (!(dateRangeValue as string)) {
                dateRangeErrors.dateRangeValue = 'Required';
            }
            const startDate = chrono.parseDate(`${dateRangeValue}`);
            values.dateRange.isOutOfMinRange = dateRangeMinValue > startDate;
            break;
        }
        case DATE_RANGES_TYPES.MONTH: {
            const monthDateRangeError: MonthDateRangeError = {};
            const { month, year } = (dateRangeValue as MonthDateRangeTypeValue) || {};
            if (!month && month !== 0) {
                monthDateRangeError.month = 'Required';
            }
            if (!year) {
                monthDateRangeError.year = 'Required';
            }
            if (!_.isEmpty(monthDateRangeError)) {
                (dateRangeErrors.dateRangeValue as MonthDateRangeError) = monthDateRangeError;
            }
            if (year !== null && month !== null) {
                const minYear = chrono.parseDate(`${dateRangeMinValue}`).getFullYear();
                const minMonth = chrono.parseDate(`${dateRangeMinValue}`).getMonth();
                if (year < minYear) {
                    values.dateRange.isOutOfMinRange = true;
                    break;
                }
                if (year === minYear) {
                    if (month < minMonth) {
                        values.dateRange.isOutOfMinRange = true;
                        break;
                    }
                }
            }
            break;
        }
        case DATE_RANGES_TYPES.YEAR: {
            if (!(dateRangeValue as number)) {
                dateRangeErrors.dateRangeValue = 'Required';
            }
            if (dateRangeValue) {
                const minYear = chrono.parseDate(`${dateRangeMinValue}`).getFullYear();
                // can safely cast dataRangeValue to number because we already checked it before
                values.dateRange.isOutOfMinRange = minYear > Number(dateRangeValue);
            }
            break;
        }
        default:
            break;
    }

    if (!_.isEmpty(dateRangeErrors)) {
        errors.dateRange = dateRangeErrors;
    }

    return errors;
};

const NewMerchantReportForm = ({ setFormValues, config, reportType }: Props) => {
    const { t } = useTranslation();
    const currencyEffectiveDate = config?.currencyEffectiveDate ? new Date(config?.currencyEffectiveDate) : new Date();
    const dateRangeMinValue: Date = new Date(new Date(currencyEffectiveDate).setHours(0, 0, 0, 0));
    const currencyChange = Boolean(config?.currencyChange);

    return (
        <Formik
            initialValues={{
                reportType: MerchantReportTypeValue.allMerchants,
                singleMerchantId: '',
                dateRange: {
                    dateRangeType: DATE_RANGES_TYPES.YEAR_TO_DATE,
                    dateRangeValue: null,
                    dateRangeMinValue,
                    isOutOfMinRange: false
                }
            }}
            validate={validate}
            onSubmit={(values) => {
                setFormValues(processFormValues(values, reportType, t));
            }}
        >
            {({
                values,
                submitForm,
                errors,
                touched,
                handleChange,
                handleBlur,
                setFieldValue,
                isValid,
                setValues
            }: FormikProps<NewMerchantReportFormValues>) => {
                return (
                    <form className={styles.formContainer}>
                        <div className={styles.reportTypeContainer}>
                            {shouldShowMerchants() && (
                                <>
                                    <Field name='reportType'>
                                        {({ field }: FieldProps) => (
                                            <ButtonGroup
                                                dataTestId='report-type-radio'
                                                className={styles.buttonGroup}
                                                name='reportType'
                                                groupselectedvalue={field.value}
                                                handleClickCallback={(value) => {
                                                    if (value === MerchantReportTypeValue.allMerchants) {
                                                        setValues({
                                                            ...values,
                                                            reportType: value,
                                                            singleMerchantId: ''
                                                        });
                                                    } else {
                                                        setFieldValue('reportType', value);
                                                    }
                                                }}
                                            >
                                                <RadioButton
                                                    inline
                                                    name='all-merchants'
                                                    value={MerchantReportTypeValue.allMerchants}
                                                    checked={field.value === MerchantReportTypeValue.allMerchants}
                                                    text={t('All Merchants')}
                                                    id='all-merchants-radio'
                                                />
                                                <RadioButton
                                                    inline
                                                    name='single-merchant'
                                                    value={MerchantReportTypeValue.singleMerchant}
                                                    checked={field.value === MerchantReportTypeValue.singleMerchant}
                                                    text={t('Single Merchant MID')}
                                                    id='single-merchants-radio'
                                                />
                                            </ButtonGroup>
                                        )}
                                    </Field>
                                    <Field name='singleMerchantId'>
                                        {({ field }: FieldProps) => (
                                            <InputBase
                                                id='singleMerchantId'
                                                value={field.value}
                                                className={styles.singleMerchantInput}
                                                name='singleMerchantId'
                                                error={!!(errors.singleMerchantId && touched.singleMerchantId)}
                                                errorMsg={errors.singleMerchantId}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                size='sm'
                                                placeholder={t('Enter MID')}
                                                maxLength={128}
                                                disabled={values.reportType !== MerchantReportTypeValue.singleMerchant}
                                                allowPan
                                            />
                                        )}
                                    </Field>
                                </>
                            )}
                            <Field name='dateRange'>
                                {({ field }: FieldProps) => (
                                    <DateRange
                                        dateRangeObj={field.value}
                                        setDateRangeObj={(newDateRange: DateRangeObject) => {
                                            setFieldValue('dateRange', newDateRange);
                                        }}
                                        dateRangeErrors={errors?.dateRange}
                                    />
                                )}
                            </Field>
                        </div>
                        <GenerateReportButton
                            extendedFormValues={values}
                            currencyChange={currencyChange}
                            setGenerateReportBtnClicked={submitForm}
                            disabled={!isValid}
                        />
                    </form>
                );
            }}
        </Formik>
    );
};

export default NewMerchantReportForm;
